import { useMemo } from 'react';

const BASE_MODIFIERS = [2, 1, 1, 0, 0, -1];

/**
 * useTraitDragDrop - Custom hook for managing trait modifier drag-and-drop interactions
 *
 * Implements auto-swap logic when dropping on filled slots
 */
const useTraitDragDrop = (traits, onTraitsUpdate) => {

  // Calculate available modifiers (not yet assigned)
  const availableModifiers = useMemo(() => {
    const assignedValues = Object.values(traits).filter(v => v !== undefined && v !== null);
    const available = [...BASE_MODIFIERS];

    // Remove assigned values from available pool
    assignedValues.forEach(value => {
      const index = available.indexOf(value);
      if (index > -1) {
        available.splice(index, 1);
      }
    });

    return available.sort((a, b) => b - a); // Sort descending: +2, +1, +1, 0, 0, -1
  }, [traits]);

  /**
   * Handle drag end (desktop mode)
   * Supports:
   * - Dragging from pool to slot
   * - Dragging from slot to slot (swaps)
   * - Dragging from slot to empty slot (moves)
   */
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return; // Dropped outside valid zone

    const draggedValue = active.data.current.value;
    const sourceSlot = active.data.current.sourceSlot; // undefined if from pool, trait name if from slot
    const targetTrait = over.id; // Trait name (e.g., "Agility")

    // Don't do anything if dropping on the same slot it came from
    if (sourceSlot === targetTrait) return;

    // Get current value in target slot
    const currentValue = traits[targetTrait];

    // Create updated traits object
    const updatedTraits = { ...traits };

    // If dragging FROM a slot (not from pool)
    if (sourceSlot) {
      // Clear the source slot
      updatedTraits[sourceSlot] = null;

      // If target slot has a value, swap it to the source slot
      if (currentValue !== undefined && currentValue !== null) {
        updatedTraits[sourceSlot] = currentValue;
      }
    }
    // If dragging from pool, no special handling needed
    // The current value in target (if any) just returns to pool automatically

    // Assign dragged value to target
    updatedTraits[targetTrait] = draggedValue;

    onTraitsUpdate(updatedTraits);
  };

  return {
    availableModifiers,
    handleDragEnd
  };
};

export default useTraitDragDrop;
